import { Component, inject, OnInit, signal } from '@angular/core';
import { formatDate, registerLocaleData } from '@angular/common';
import esCo from '@angular/common/locales/es-CO';
import { HttpClient, HttpContext } from '@angular/common/http';
import { RouterLink } from '@angular/router';
import { catchError, forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { PageTitleService } from '../../../shared/services';
import { Professional, Service } from '../../../shared/interfaces';
import { AuthService } from '../../../services/auth';
import { BusinessService } from '../../../services/business';
import { BusinessScheduleRange, ScheduleService } from '../../../services/business/schedule.service';
import { MembershipService } from '../../../services/membership/membership.service';
import { MessagePackagesService } from '../../../services/message-packages.service';
import { DashboardService } from '../../../services/dashboard.service';
import { TeamService } from '../../../services/team/team.service';
import { REQUIRE_AUTH } from '../../../shared/interceptors/auth-context';
import { environment } from '../../../../environments/environment';

type TileIcon = 'clock' | 'services' | 'team' | 'users' | 'card' | 'chat' | 'chart';
type Tone = 'ok' | 'warning' | 'danger';

/** What a tile says about its setting right now */
export interface TileStatus {
  value: string;
  note?: string;
  tone?: Tone;
  /** 0–100, drawn as a usage bar */
  meter?: number;
  /** Plan seats, one entry per paid seat */
  seats?: { taken: boolean }[];
}

interface SettingsTile {
  key: string;
  title: string;
  route: string;
  icon: TileIcon;
  /** Shown while loading or when the status can't be fetched */
  fallback: string;
}

registerLocaleData(esCo);

const DAY_SHORT = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];
/** Monday-first order, as the business hours page shows the week */
const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0];

@Component({
  selector: 'app-general',
  imports: [RouterLink],
  templateUrl: './general.html',
  styleUrl: './general.css',
})
export class General implements OnInit {
  titleService = inject(PageTitleService);
  private authService = inject(AuthService);
  private businessService = inject(BusinessService);
  private scheduleService = inject(ScheduleService);
  private membershipService = inject(MembershipService);
  private messagesService = inject(MessagePackagesService);
  private dashboardService = inject(DashboardService);
  private teamService = inject(TeamService);
  private http = inject(HttpClient);
  private apiBaseUrl = environment.apiUrl;

  readonly tiles: SettingsTile[] = [
    { key: 'hours', title: 'Horarios', route: '/settings/business-hours', icon: 'clock', fallback: 'Días y horas de atención' },
    { key: 'services', title: 'Servicios', route: '/settings/services', icon: 'services', fallback: 'Lo que ofreces' },
    { key: 'team', title: 'Profesionales', route: '/settings/professionals', icon: 'team', fallback: 'Tu equipo' },
    ...(this.authService.has('users.view')
      ? [{ key: 'users', title: 'Usuarios', route: '/settings/users', icon: 'users' as TileIcon, fallback: 'Quién entra y con qué rol' }]
      : []),
    { key: 'plan', title: 'Membresía', route: '/settings/billing', icon: 'card', fallback: 'Tu plan y pagos' },
    { key: 'messages', title: 'Mensajes de WhatsApp', route: '/settings/messages', icon: 'chat', fallback: 'Mensajes y paquetes' },
    { key: 'reports', title: 'Reportes', route: '/settings/reports', icon: 'chart', fallback: 'Métricas de tu negocio' },
  ];

  /** Status per tile key; undefined while loading, null when it couldn't load */
  readonly status = signal<Record<string, TileStatus | null | undefined>>({});

  ngOnInit(): void {
    this.titleService.setPage({
      title: 'Configuración',
      subtitle: 'Cómo está tu negocio hoy y dónde ajustarlo',
    });

    const businessId = this.authService.getUserInfo()?.business_id;
    if (!businessId) {
      this.tiles.forEach((t) => this.setStatus(t.key, null));
      return;
    }

    this.track('hours', this.loadHours(businessId));
    this.track('services', this.loadServices(businessId));
    this.track('team', this.loadTeam(businessId));
    if (this.tiles.some((t) => t.key === 'users')) {
      this.track('users', this.loadUsers());
    }
    this.track('plan', this.loadPlan());
    this.track('messages', this.loadMessages());
    this.track('reports', this.loadReports());
  }

  /** Tiles that need attention, to summarize them above the grid */
  get attentionCount(): number {
    return Object.values(this.status()).filter((s) => s && s.tone && s.tone !== 'ok').length;
  }

  private track(key: string, source: Observable<TileStatus>) {
    source.pipe(catchError(() => of(null))).subscribe((s) => this.setStatus(key, s));
  }

  private setStatus(key: string, value: TileStatus | null) {
    this.status.update((current) => ({ ...current, [key]: value }));
  }

  // ---- Loaders ----

  private loadHours(businessId: string): Observable<TileStatus> {
    return this.scheduleService
      .getBusinessSchedule(businessId)
      .pipe(map((res) => this.summarizeHours(res.timeRanges ?? [])));
  }

  private loadServices(businessId: string): Observable<TileStatus> {
    return this.businessService
      .getRecordsByBusinessId<Service>(businessId, { page: 1, pageSize: 100, isActive: true }, 'services')
      .pipe(
        switchMap((res: any) => {
          const services: Service[] = res.services ?? [];
          const total: number = res.totalCount ?? services.length;
          if (total === 0) {
            return of<TileStatus>({ value: 'Sin servicios', note: 'Crea uno para recibir reservas', tone: 'warning' });
          }
          const value = `${total} servicio${total !== 1 ? 's' : ''}`;
          const teams = services.map((s) =>
            this.http
              .get<Professional[]>(
                `${this.apiBaseUrl}/api/v1/businesses/${businessId}/services/${s.serviceId}/professionals`,
                { context: new HttpContext().set(REQUIRE_AUTH, true) }
              )
              .pipe(catchError(() => of(null)))
          );
          if (teams.length === 0) return of<TileStatus>({ value });
          return forkJoin(teams).pipe(
            map((results): TileStatus => {
              const unstaffed = results.filter((r) => r !== null && r.length === 0).length;
              return unstaffed > 0
                ? { value, note: `${unstaffed} sin profesional asignado`, tone: 'warning' }
                : { value };
            })
          );
        })
      );
  }

  private loadUsers(): Observable<TileStatus> {
    return this.teamService.getUsers().pipe(
      map((res): TileStatus => {
        const total = res.users.length;
        const pending = res.invitations.filter((i) => i.status === 'pending').length;
        return {
          value: `${total} usuario${total !== 1 ? 's' : ''}`,
          note: pending ? `${pending} invitación${pending !== 1 ? 'es' : ''} sin aceptar` : undefined,
        };
      })
    );
  }

  private loadTeam(businessId: string): Observable<TileStatus> {
    const isAdmin = this.authService.isSystemAdmin();
    return forkJoin({
      res: this.businessService.getRecordsByBusinessId<Professional>(
        businessId,
        { page: 1, pageSize: 100 },
        'professionals'
      ),
      paid: isAdmin ? of(0) : this.membershipService.getPaidProfessionalsCount(),
    }).pipe(map(({ res, paid }) => this.summarizeTeam(res as any, paid)));
  }

  private loadPlan(): Observable<TileStatus> {
    return this.membershipService.getMembershipStatus().pipe(
      map((m): TileStatus => {
        const plan = m.planName || 'Sin plan';
        switch (m.status) {
          case 'trial': {
            const days = m.trialDaysRemaining ?? 0;
            return {
              value: 'Periodo de prueba',
              note: days > 0 ? `Te quedan ${days} día${days !== 1 ? 's' : ''}` : 'Termina hoy',
              tone: days <= 5 ? 'warning' : 'ok',
            };
          }
          case 'active':
            return {
              value: plan,
              note: m.nextBillingDate
                ? `Se renueva el ${formatDate(m.nextBillingDate, "d 'de' MMM", 'es-CO')}`
                : undefined,
            };
          case 'expired':
            return { value: plan, note: 'Venció; renueva para seguir recibiendo citas', tone: 'danger' };
          case 'suspended':
            return { value: plan, note: 'Suspendida por pago pendiente', tone: 'danger' };
          case 'cancelled':
            return { value: plan, note: 'Cancelada', tone: 'warning' };
          default:
            return { value: 'Sin plan', note: 'Elige un plan para empezar', tone: 'warning' };
        }
      })
    );
  }

  private loadMessages(): Observable<TileStatus> {
    return this.messagesService.getStatus().pipe(
      map((s): TileStatus => {
        const total = (s.freeQuotaLimit ?? 0) + (s.packageTotalCredits ?? 0);
        const left = s.totalRemainingCredits ?? 0;
        if (total === 0) return { value: `${left} disponibles` };
        const share = left / total;
        return {
          value: `${left} de ${total} disponibles`,
          meter: Math.round(share * 100),
          note: left === 0 ? 'Se acabaron; compra un paquete' : share < 0.1 ? 'Te quedan pocos' : undefined,
          tone: left === 0 ? 'danger' : share < 0.1 ? 'warning' : 'ok',
        };
      })
    );
  }

  private loadReports(): Observable<TileStatus> {
    return this.dashboardService.getMetrics().pipe(
      map((m): TileStatus => {
        const n = m.week?.totalAppointments ?? 0;
        const occupancy = m.week?.occupancyPercentage;
        return {
          value: `${n} cita${n !== 1 ? 's' : ''} esta semana`,
          note: occupancy != null ? `Ocupación del ${Math.round(occupancy)} %` : undefined,
        };
      })
    );
  }

  // ---- Summaries ----

  /** "Lun a sáb, 9:00–19:00" when every open day shares one range; otherwise days and total hours */
  summarizeHours(ranges: BusinessScheduleRange[]): TileStatus {
    const active = ranges.filter((r) => r.isActive);
    if (active.length === 0) {
      return { value: 'Sin horarios', note: 'Configúralos para recibir citas', tone: 'warning' };
    }

    const byDay = new Map<number, BusinessScheduleRange[]>();
    active.forEach((r) => byDay.set(r.dayOfWeek, [...(byDay.get(r.dayOfWeek) ?? []), r]));
    const openDays = WEEK_ORDER.filter((d) => byDay.has(d));
    const signature = (d: number) =>
      byDay
        .get(d)!
        .map((r) => `${this.shortTime(r.startTime)}–${this.shortTime(r.endTime)}`)
        .sort()
        .join(', ');

    const first = signature(openDays[0]);
    const uniform = openDays.every((d) => signature(d) === first);
    const days = this.describeDays(openDays);

    if (uniform) {
      return { value: `${days}, ${first}` };
    }
    const minutes = active.reduce((sum, r) => sum + this.minutes(r.endTime) - this.minutes(r.startTime), 0);
    const hours = Math.round(minutes / 60);
    return { value: days, note: `Horarios variados, ${hours} h a la semana` };
  }

  private describeDays(days: number[]): string {
    if (days.length === 7) return 'Todos los días';
    const idx = days.map((d) => WEEK_ORDER.indexOf(d));
    const consecutive = idx.every((v, i) => i === 0 || v === idx[i - 1] + 1);
    const label =
      consecutive && days.length > 2
        ? `${DAY_SHORT[days[0]]} a ${DAY_SHORT[days[days.length - 1]]}`
        : days.map((d) => DAY_SHORT[d]).join(', ');
    return label.charAt(0).toUpperCase() + label.slice(1);
  }

  /** "09:00:00" → "9:00" */
  private shortTime(time: string): string {
    return time.slice(0, 5).replace(/^0(\d)/, '$1');
  }

  private minutes(time: string): number {
    const [h, m] = time.split(':').map(Number);
    return h * 60 + (m || 0);
  }

  summarizeTeam(res: { professionals?: Professional[]; activeCount?: number; totalCount?: number }, paid: number): TileStatus {
    const pros = res.professionals ?? [];
    const active = res.activeCount ?? pros.filter((p) => p.isActive).length;

    if (active === 0) {
      return { value: 'Sin profesionales activos', note: 'Agrega a tu equipo', tone: 'warning' };
    }
    if (paid <= 0) {
      return { value: `${active} activo${active !== 1 ? 's' : ''}` };
    }

    const seatCount = Math.max(paid, active);
    const seats = Array.from({ length: seatCount }, (_, i) => ({ taken: i < active }));
    const free = paid - active;
    return {
      value: `${active} de ${paid} cupos en uso`,
      seats,
      note: free <= 0 ? 'Todos los cupos ocupados' : `${free} libre${free !== 1 ? 's' : ''}`,
    };
  }
}
